import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal } from '../shared';
import { ocrApi } from './ocrApi';
import OcrSettings, { DEFAULT_SETTINGS, settingsToParams } from './OcrSettings';
import OcrPageView, { FILTER_LABELS } from './OcrPageView';
import ApiDocsContent from './ApiDocsContent';
import OcrGetCode from './OcrGetCode';
import OcrSourcePreview from './OcrSourcePreview';
import OcrSpottingView from './OcrSpottingView';

const STATUS_MAP = { 0: 'pending', 1: 'queued', 2: 'processing', 5: 'completed', 9: 'failed' };

export default function AdminOCR() {
    const [files, setFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState(new Set());
    const [taskMap, setTaskMap] = useState({});
    const [activeFile, setActiveFile] = useState(null);
    const [activeResult, setActiveResult] = useState(null);
    const [activePage, setActivePage] = useState(0);
    const [resultTab, setResultTab] = useState('markdown');
    const [contentFilters, setContentFilters] = useState(
        Object.fromEntries(FILTER_LABELS.map(f => [f.id, true]))
    );
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [showSettings, setShowSettings] = useState(false);
    const [showApiDocs, setShowApiDocs] = useState(false);
    const [showGetCode, setShowGetCode] = useState(false);
    const [showMonitor, setShowMonitor] = useState(false);
    const [monitorData, setMonitorData] = useState(null);
    const [hoveredBlockIdx, setHoveredBlockIdx] = useState(null);
    const [selectedBlockIdx, setSelectedBlockIdx] = useState(null);
    const [showSourcePreview, setShowSourcePreview] = useState(true);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const pollRef = useRef(null);
    const pollIdsRef = useRef([]);
    const activeFileRef = useRef(null);
    const leftScrollRef = useRef(null);
    const rightScrollRef = useRef(null);
    const scrollSyncRef = useRef(false);
    const pollFnRef = useRef(null);
    const elapsedRef = useRef(null);
    const fileInputRef = useRef(null);

    const addFiles = useCallback((newFiles) => {
        const toAdd = [...newFiles].filter(f => f.size <= 50 * 1024 * 1024);
        setFiles(prev => {
            const existing = new Set(prev.map(f => f.name + f.size));
            const filtered = toAdd.filter(f => !existing.has(f.name + f.size));
            return [...prev, ...filtered];
        });
        setSelectedFiles(prev => {
            const next = new Set(prev);
            toAdd.forEach(f => next.add(f.name));
            return next;
        });
    }, []);

    const removeFile = useCallback((idx) => {
        setFiles(prev => {
            const removed = prev[idx];
            setSelectedFiles(s => { const n = new Set(s); n.delete(removed.name); return n; });
            return prev.filter((_, i) => i !== idx);
        });
    }, []);

    const toggleSelect = useCallback((name) => {
        setSelectedFiles(prev => {
            const n = new Set(prev);
            n.has(name) ? n.delete(name) : n.add(name);
            return n;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedFiles(prev => prev.size === files.length ? new Set() : new Set(files.map(f => f.name)));
    }, [files]);

    const getFilesToProcess = useCallback(() => {
        return files.filter(f => {
            if (!selectedFiles.has(f.name)) return false;
            if (settings.force) return true;
            const t = taskMap[f.name];
            return !t || t.taskStatus !== 5;
        });
    }, [files, selectedFiles, taskMap, settings.force]);

    const startBatch = useCallback(async () => {
        const toProcess = getFilesToProcess();
        if (!toProcess.length) return;
        setLoading(true);
        setStartTime(Date.now());

        setError(null);
        try {
            const res = await ocrApi.createBatch(toProcess, settingsToParams(settings));
            if (res.errorCode !== 0) throw new Error(res.errorMsg || 'Batch create failed');

            setTaskMap(prev => {
                const next = { ...prev };
                res.result.tasks.forEach(t => {
                    next[t.fileName] = { ...t, statusText: t.cached ? 'completed' : 'queued' };
                });
                return next;
            });
            const newIds = res.result.tasks.map(t => t.taskId);
            const existingIds = Object.values(taskMap).filter(t => t.taskId && t.taskStatus < 5).map(t => t.taskId);
            startPolling([...new Set([...existingIds, ...newIds])]);
        } catch (e) {
            setError(e.message || 'Failed to start batch');
            setStartTime(null);
        }
        setLoading(false);
    }, [getFilesToProcess, settings, taskMap]);

    const runSingleFile = useCallback(async (fileName, force = false) => {
        const file = files.find(f => f.name === fileName);
        if (!file) return;
        setContextMenu(null);
        setError(null);
        setStartTime(Date.now());

        const params = { ...settingsToParams(settings), force };
        try {
            const res = await ocrApi.createBatch([file], params);
            if (res.errorCode !== 0) throw new Error(res.errorMsg || 'Failed');

            setTaskMap(prev => {
                const next = { ...prev };
                res.result.tasks.forEach(t => {
                    next[t.fileName] = { ...t, statusText: t.cached ? 'completed' : 'queued' };
                });
                return next;
            });
            const allIds = [
                ...Object.values(taskMap).filter(t => t.taskId && t.taskStatus < 5).map(t => t.taskId),
                ...res.result.tasks.map(t => t.taskId),
            ];
            startPolling([...new Set(allIds)]);
        } catch (e) {
            setError(e.message);
            setStartTime(null);
        }
    }, [files, settings, taskMap]);

    const stopPolling = useCallback(() => {
        if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    }, []);

    pollFnRef.current = async () => {
        const taskIds = pollIdsRef.current;
        if (!taskIds.length) return;
        try {
            const res = await ocrApi.batchStatus(taskIds);
            if (res.errorCode !== 0) return;

            const completedNew = [];
            setTaskMap(prev => {
                const next = { ...prev };
                res.result.forEach(t => {
                    if (next[t.fileName]) {
                        const wasNotDone = next[t.fileName].taskStatus < 5;
                        next[t.fileName] = { ...next[t.fileName], taskStatus: t.taskStatus, totalPages: t.totalPages, extractedPages: t.extractedPages };
                        if (wasNotDone && t.taskStatus === 5) completedNew.push(t);
                    }
                });
                const allDone = res.result.every(t => t.taskStatus >= 5);
                if (allDone) {
                    stopPolling();
                    const firstCompleted = res.result.find(t => t.taskStatus === 5);
                    if (firstCompleted && !activeFileRef.current) {
                        setActiveFile(firstCompleted.fileName);
                    }
                }
                return next;
            });
            for (const t of completedNew) {
                try {
                    const detail = await ocrApi.taskDetail(t.taskId);
                    if (detail.errorCode === 0 && detail.result?.inferenceTime) {
                        setTaskMap(prev => ({
                            ...prev,
                            [t.fileName]: { ...prev[t.fileName], inferenceTime: detail.result.inferenceTime },
                        }));
                    }
                    if (detail.errorCode === 0 && t.fileName === activeFileRef.current) {
                        viewResult(t.taskId);
                    }
                } catch {}
            }
        } catch {}
    };

    const startPolling = useCallback((taskIds) => {
        stopPolling();
        pollIdsRef.current = taskIds;
        const loop = async () => {
            await pollFnRef.current();
            pollRef.current = setTimeout(loop, 3000);
        };
        loop();
    }, [stopPolling]);

    useEffect(() => {
        if (startTime && Object.values(taskMap).some(t => t.taskStatus < 5)) {
            elapsedRef.current = setInterval(() => setElapsed(Date.now() - startTime), 200);
        } else if (elapsedRef.current) {
            clearInterval(elapsedRef.current);
        }
        return () => { if (elapsedRef.current) clearInterval(elapsedRef.current); };
    }, [startTime, taskMap]);

    const viewResult = useCallback(async (taskId) => {
        try {
            const res = await ocrApi.taskDetail(taskId);
            if (res.errorCode !== 0) return;
            setActiveResult(res.result);
            setActivePage(0);
            const r = res.result;
            if (r.taskStatus === 5 && r.fileName) {
                setTaskMap(prev => {
                    if (!prev[r.fileName] || prev[r.fileName].taskStatus === 5) return prev;
                    return {
                        ...prev,
                        [r.fileName]: {
                            ...prev[r.fileName],
                            taskStatus: 5,
                            totalPages: r.totalPages,
                            extractedPages: r.extractedPages || r.totalPages,
                            inferenceTime: r.inferenceTime,
                        },
                    };
                });
            }
        } catch {}
    }, []);

    useEffect(() => { activeFileRef.current = activeFile; }, [activeFile]);

    useEffect(() => { return () => stopPolling(); }, []);

    const [cacheStats, setCacheStats] = useState(null);
    const [showCacheMenu, setShowCacheMenu] = useState(false);

    const loadMonitor = useCallback(async () => {
        try {
            const res = await ocrApi.monitor();
            setMonitorData(res);
        } catch {}
    }, []);

    const loadCacheStats = useCallback(async () => {
        try {
            const res = await ocrApi.cacheStats();
            setCacheStats(res);
        } catch {}
    }, []);

    const clearCache = useCallback(async (pattern = null) => {
        try {
            await ocrApi.cacheClear(pattern);
            setShowCacheMenu(false);
            loadCacheStats();
        } catch {}
    }, [loadCacheStats]);

    const tasks = Object.values(taskMap);
    const completed = tasks.filter(t => t.taskStatus === 5).length;
    const processing = tasks.filter(t => t.taskStatus === 2).length;
    const totalInferenceTime = tasks.reduce((sum, t) => sum + (t.inferenceTime || 0), 0);
    const failed = tasks.filter(t => t.taskStatus === 9).length;
    const pages = activeResult?.parsingResult || [];
    const currentPage = pages[activePage];
    const activeSourceFile = activeFile ? files.find(f => f.name === activeFile) : null;

    const handleDrop = (e) => { e.preventDefault(); addFiles(e.dataTransfer.files); };

    useEffect(() => {
        const close = () => { setContextMenu(null); setShowCacheMenu(false); };
        if (contextMenu || showCacheMenu) window.addEventListener('click', close);
        return () => window.removeEventListener('click', close);
    }, [contextMenu, showCacheMenu]);

    return (
        <div ref={el => {
            if (el) {
                const parent = el.closest('.max-w-7xl');
                if (parent) { parent.style.maxWidth = '100%'; parent.style.padding = '0'; parent.style.margin = '0'; }
            }
        }} className="flex flex-col" style={{ height: '100vh', background: '#fff' }}>

            {showSettings && (
                <div className="fixed inset-0 z-40" onClick={() => setShowSettings(false)}>
                    <div className="absolute inset-0 bg-black/20" />
                    <div
                        className="absolute right-0 top-0 h-full overflow-y-auto shadow-xl"
                        style={{ width: '360px', background: '#fff', borderLeft: '1px solid #e1e6f8', padding: '20px' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#140e35' }}>Settings</h4>
                            <button onClick={() => setShowSettings(false)} style={{ color: '#9498ac', fontSize: '18px', cursor: 'pointer', background: 'none', border: 'none' }}>×</button>
                        </div>
                        <OcrSettings settings={settings} onChange={setSettings} />
                    </div>
                </div>
            )}

            {tasks.length > 0 && (
                <div className="flex items-center gap-4 text-xs flex-shrink-0" style={{ color: '#565772', background: '#f7f9ff', borderBottom: '1px solid #e1e6f8', padding: '4px 16px' }}>
                    <span>Files: <b style={{ color: '#140e35' }}>{tasks.length}</b></span>
                    <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Done: <b style={{ color: '#140e35' }}>{completed}</b></span>
                    <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#4658ff' }} /> Running: <b style={{ color: '#140e35' }}>{processing}</b></span>
                    {failed > 0 && <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#FF4040' }} /> Failed: <b style={{ color: '#140e35' }}>{failed}</b></span>}
                    {startTime && <span>Elapsed: <b style={{ color: '#140e35' }}>{(elapsed / 1000).toFixed(1)}s</b></span>}
                    {totalInferenceTime > 0 && <span>Inference: <b style={{ color: '#140e35' }}>{totalInferenceTime.toFixed(1)}s</b></span>}
                    <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: '#e1e6f8' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.round((completed + failed) / tasks.length * 100)}%`, background: '#2FBD71' }} />
                    </div>
                </div>
            )}

            {error && (
                <div className="flex items-center gap-2 text-xs flex-shrink-0" style={{ background: '#fff3f1', border: '1px solid #FF4040', color: '#FF4040', padding: '8px 20px', borderRadius: '0' }}>
                    <span className="font-medium">Error:</span> {error}
                    <button className="ml-auto hover:opacity-60" onClick={() => setError(null)}>×</button>
                </div>
            )}

            <div className="flex flex-1 min-h-0" style={{ gap: '0' }}>
                <div className="flex-shrink-0 flex flex-col overflow-hidden" style={{ width: '260px', background: '#fff', borderRight: '1px solid #e1e6f8' }}>
                    <div style={{ padding: '12px', borderBottom: '1px solid #e1e6f8' }}>
                        <button
                            className="w-full text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
                            style={{ background: '#4658ff', color: '#fff', padding: '10px 0', border: 'none', cursor: 'pointer' }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            + New Parsing
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.png,.jpg,.jpeg,.bmp,.webp"
                            className="hidden"
                            onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                        />
                    </div>

                    <div
                        className="flex-1 overflow-y-auto"
                        onDragOver={e => e.preventDefault()}
                        onDrop={handleDrop}
                    >
                        {files.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs p-4">
                                <div className="text-3xl mb-2">📄</div>
                                <p>Drop files here</p>
                            </div>
                        ) : (
                            files.map((f, i) => {
                                const t = taskMap[f.name];
                                const st = t ? STATUS_MAP[t.taskStatus] || 'pending' : 'ready';
                                const isActive = activeFile === f.name;
                                const isSelected = selectedFiles.has(f.name);
                                return (
                                    <div
                                        key={f.name + f.size}
                                        className="relative cursor-pointer transition-colors"
                                        style={{
                                            padding: '10px 12px',
                                            borderBottom: '1px solid #f0f1f5',
                                            borderLeft: isActive ? '3px solid #4658ff' : '3px solid transparent',
                                            background: isActive ? '#eef1ff' : 'transparent',
                                        }}
                                        onClick={() => {
                                            setActiveFile(f.name);
                                            setActivePage(0);
                                            setHoveredBlockIdx(null);
                                            setSelectedBlockIdx(null);
                                            if (t?.taskStatus === 5 && t.taskId) viewResult(t.taskId);
                                            else setActiveResult(null);
                                        }}
                                        onContextMenu={e => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, fileName: f.name, status: st }); }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                readOnly
                                                onClick={e => { e.stopPropagation(); toggleSelect(f.name); }}
                                                className="accent-blue-600 w-3.5 h-3.5 flex-shrink-0 cursor-pointer"
                                            />
                                            <span className="text-sm">{f.name.endsWith('.pdf') ? '📄' : '🖼️'}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-medium truncate" style={{ color: '#140e35' }}>{f.name}</div>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: st === 'completed' ? '#2FBD71' : st === 'processing' ? '#4658ff' : st === 'failed' ? '#FF4040' : '#aeb4c9', animation: st === 'processing' ? 'pulse 2s infinite' : 'none' }} />
                                                    <span style={{ fontSize: '10px', color: '#9498ac' }}>{st}</span>
                                                    {t?.totalPages > 0 && <span style={{ fontSize: '10px', color: '#9498ac' }}>· {t.extractedPages}/{t.totalPages}p</span>}
                                                    {t?.inferenceTime && <span style={{ fontSize: '10px', color: '#4658ff' }}>· {t.inferenceTime}s</span>}
                                                </div>
                                            </div>
                                            {!t && <button style={{ color: '#aeb4c9', fontSize: '14px' }} className="hover:opacity-60" onClick={e => { e.stopPropagation(); removeFile(i); }}>×</button>}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div style={{ padding: '10px 12px', borderTop: '1px solid #e1e6f8' }} className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-1.5 cursor-pointer" style={{ fontSize: '11px', color: '#9498ac' }}>
                                <input type="checkbox" checked={selectedFiles.size === files.length && files.length > 0} onChange={toggleSelectAll} style={{ accentColor: '#4658ff' }} className="w-3 h-3" />
                                Select all
                            </label>
                            <span style={{ fontSize: '11px', color: '#9498ac' }}>{selectedFiles.size}/{files.length} selected</span>
                        </div>
                        <div className="flex gap-2">
                            <button
                                className="flex-1 text-xs font-medium rounded-md hover:opacity-90 transition-opacity disabled:opacity-40"
                                style={{ background: '#4658ff', color: '#fff', padding: '8px 0', border: 'none', cursor: 'pointer' }}
                                onClick={startBatch}
                                disabled={!getFilesToProcess().length || loading}
                            >
                                {loading ? 'Starting...' : `Start (${getFilesToProcess().length} new)`}
                            </button>
                            <button
                                className="text-xs font-medium rounded-md hover:opacity-80 transition-opacity"
                                style={{ background: '#e5e9f3', color: '#140e35', padding: '8px 12px', border: 'none', cursor: 'pointer' }}
                                onClick={() => { stopPolling(); setFiles([]); setTaskMap({}); setSelectedFiles(new Set()); setActiveResult(null); setActiveFile(null); }}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 flex flex-col overflow-hidden" style={{ background: '#fff', borderLeft: '1px solid #e1e6f8' }}>
                    {(() => {
                        const activeTask = activeFile ? taskMap[activeFile] : null;
                        const isProcessing = activeTask && activeTask.taskStatus < 5;
                        const hasResult = activeResult && activeResult.taskStatus === 5;

                        return (
                            <>
                                <div className="flex items-center justify-between flex-shrink-0" style={{ padding: '6px 12px', borderBottom: '1px solid #e1e6f8' }}>
                                    <div className="flex items-center" style={{ gap: '2px', border: '1px solid #e5eaf4', borderRadius: '30px', padding: '3px', background: '#fff' }}>
                                        {[
                                            { id: 'markdown', label: 'Document Parsing' },
                                            { id: 'json', label: 'JSON' },
                                            { id: 'info', label: 'Info' },
                                        ].map(tab => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setResultTab(tab.id)}
                                                className="transition-colors"
                                                style={{
                                                    padding: '5px 14px',
                                                    borderRadius: '22px',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    border: resultTab === tab.id ? '1px solid #4658ff' : '1px solid transparent',
                                                    background: resultTab === tab.id ? '#eef1ff' : 'transparent',
                                                    color: resultTab === tab.id ? '#4658ff' : '#565772',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button title="Settings" className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 transition-colors" style={{ color: '#565772', cursor: 'pointer', background: 'none', border: 'none' }} onClick={() => setShowSettings(!showSettings)}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
                                        </button>
                                        <button title="Monitor" className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 transition-colors" style={{ color: '#565772', cursor: 'pointer', background: 'none', border: 'none' }} onClick={() => { setShowMonitor(true); loadMonitor(); }}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                                        </button>
                                        <button title="Get Code" className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 transition-colors" style={{ color: '#565772', cursor: 'pointer', background: 'none', border: 'none' }} onClick={() => setShowGetCode(true)}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                                        </button>
                                        <button title="API Docs" className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 transition-colors" style={{ color: '#565772', cursor: 'pointer', background: 'none', border: 'none' }} onClick={() => setShowApiDocs(true)}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                        </button>
                                        <div className="relative" onClick={e => e.stopPropagation()}>
                                            <button title="Cache" className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 transition-colors" style={{ color: '#565772', cursor: 'pointer', background: 'none', border: 'none' }} onClick={() => { setShowCacheMenu(s => !s); if (!showCacheMenu) loadCacheStats(); }}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/></svg>
                                            </button>
                                            {showCacheMenu && (
                                                <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg py-2 w-52" style={{ border: '1px solid #e1e6f8' }}>
                                                    {cacheStats && (
                                                        <div className="px-3 py-1.5 mb-1" style={{ borderBottom: '1px solid #e1e6f8' }}>
                                                            <div className="grid grid-cols-2 gap-1" style={{ fontSize: '10px' }}>
                                                                <span style={{ color: '#9498ac' }}>Tasks:</span><span style={{ color: '#140e35', fontWeight: 500 }}>{cacheStats.tasks}</span>
                                                                <span style={{ color: '#9498ac' }}>Cache:</span><span style={{ color: '#140e35', fontWeight: 500 }}>{cacheStats.cache_entries}</span>
                                                                <span style={{ color: '#9498ac' }}>Queue:</span><span style={{ color: '#140e35', fontWeight: 500 }}>{cacheStats.queue}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50" style={{ color: '#565772' }} onClick={() => clearCache('cache')}>Clear file cache</button>
                                                    <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50" style={{ color: '#565772' }} onClick={() => clearCache('tasks')}>Clear tasks</button>
                                                    <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50" style={{ color: '#565772' }} onClick={() => clearCache('queue')}>Clear queue</button>
                                                    <div style={{ borderTop: '1px solid #e1e6f8', marginTop: '4px', paddingTop: '4px' }}>
                                                        <button className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50" style={{ color: '#FF4040' }} onClick={() => clearCache()}>Clear ALL</button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ width: '1px', height: '16px', background: '#e1e6f8', margin: '0 2px' }} />
                                        {activeSourceFile && (
                                            <button title="Source File" onClick={() => setShowSourcePreview(v => !v)} className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 transition-colors" style={{ color: showSourcePreview ? '#4658ff' : '#565772', cursor: 'pointer', background: showSourcePreview ? '#eef1ff' : 'none', border: 'none' }}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="2"/><line x1="12" y1="2" x2="12" y2="22"/></svg>
                                            </button>
                                        )}
                                        <button title={copied ? 'Copied!' : 'Copy'} className="flex items-center justify-center w-7 h-7 rounded-md transition-colors" style={{ color: copied ? '#2FBD71' : hasResult ? '#565772' : '#d9dee9', cursor: hasResult ? 'pointer' : 'default', background: 'none', border: 'none' }}
                                            disabled={!hasResult}
                                            onClick={() => {
                                                if (!hasResult) return;
                                                let text = '';
                                                if (resultTab === 'markdown') {
                                                    const hidden = new Set(FILTER_LABELS.filter(f => !contentFilters[f.id]).map(f => f.id));
                                                    text = pages.map((p, i) => {
                                                        const blocks = p?.blocks || [];
                                                        const md = blocks.length
                                                            ? blocks.filter(b => !hidden.has(b.block_label)).sort((a, b) => (a.block_order ?? 9999) - (b.block_order ?? 9999)).map(b => b.block_content || '').join('\n\n')
                                                            : p?.markdown || '';
                                                        return `## Page ${i + 1}\n\n${md}`;
                                                    }).join('\n\n---\n\n');
                                                } else {
                                                    text = JSON.stringify(resultTab === 'json' ? pages : activeResult, null, 2);
                                                }
                                                navigator.clipboard.writeText(text);
                                                setCopied(true);
                                                setTimeout(() => setCopied(false), 2000);
                                            }}
                                        >
                                            {copied ? (
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                            ) : (
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                            )}
                                        </button>
                                        <button title="Download Markdown" className="flex items-center justify-center w-7 h-7 rounded-md transition-colors" style={{ color: hasResult ? '#565772' : '#d9dee9', cursor: hasResult ? 'pointer' : 'default', background: 'none', border: 'none' }}
                                            disabled={!hasResult}
                                            onClick={() => {
                                                if (!hasResult) return;
                                                const md = pages.map((p, i) => `\n---\n## Page ${i + 1}\n\n${p.markdown || ''}`).join('\n');
                                                const blob = new Blob([md], { type: 'text/markdown' });
                                                const a = document.createElement('a');
                                                a.href = URL.createObjectURL(blob);
                                                a.download = ((activeResult || {}).fileName || 'result').replace(/\.[^.]+$/, '') + '.md';
                                                a.click();
                                            }}
                                        >
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                        </button>
                                        {hasResult && pages.some(p => p?.spottingRes) && (
                                            <button title="Download Layout Text" className="flex items-center justify-center w-7 h-7 rounded-md transition-colors hover:bg-gray-100" style={{ color: '#565772', cursor: 'pointer', background: 'none', border: 'none' }}
                                                onClick={() => {
                                                    const parts = [];
                                                    pages.forEach((p, pi) => {
                                                        const sr = p.spottingRes;
                                                        if (!sr) { parts.push(`--- Page ${pi+1} ---\n${p.markdown || ''}`); return; }
                                                        const texts = sr.rec_texts || [];
                                                        const polys = sr.rec_polys || [];
                                                        const pw = p.pageWidth || 1080;
                                                        const charW = pw / 80;
                                                        const regions = texts.map((t, i) => {
                                                            const poly = polys[i];
                                                            if (!poly || !t.trim()) return null;
                                                            const minX = Math.min(...poly.map(pt => pt[0]));
                                                            const minY = Math.min(...poly.map(pt => pt[1]));
                                                            const maxY = Math.max(...poly.map(pt => pt[1]));
                                                            return { text: t.trim(), x: minX, cy: (minY+maxY)/2, h: maxY-minY };
                                                        }).filter(Boolean).sort((a,b) => a.cy - b.cy);
                                                        const rows = []; let cur = [];
                                                        regions.forEach(r => {
                                                            if (!cur.length) { cur.push(r); return; }
                                                            const avgCy = cur.reduce((s,rr) => s+rr.cy, 0) / cur.length;
                                                            const maxH = Math.max(...cur.map(rr => rr.h));
                                                            if (Math.abs(r.cy - avgCy) < maxH * 0.6) cur.push(r);
                                                            else { rows.push(cur); cur = [r]; }
                                                        });
                                                        if (cur.length) rows.push(cur);
                                                        const lines = rows.map(row => {
                                                            row.sort((a,b) => a.x - b.x);
                                                            let line = '', cursor = 0;
                                                            row.forEach(r => {
                                                                const col = Math.floor(r.x / charW);
                                                                line += ' '.repeat(Math.max(1, col - cursor)) + r.text;
                                                                cursor = col + r.text.length;
                                                            });
                                                            return line;
                                                        });
                                                        parts.push(`--- Page ${pi+1} ---\n${lines.join('\n')}`);
                                                    });
                                                    const blob = new Blob([parts.join('\n\n')], { type: 'text/plain' });
                                                    const a = document.createElement('a');
                                                    a.href = URL.createObjectURL(blob);
                                                    a.download = (activeResult.fileName || 'result').replace(/\.[^.]+$/, '') + '_layout.txt';
                                                    a.click();
                                                }}
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {resultTab === 'markdown' && (
                                    <>
                                        <div className="flex items-center gap-3 flex-wrap flex-shrink-0" style={{ padding: '4px 12px', borderBottom: '1px solid #e1e6f8', background: '#f7f9ff' }}>
                                            <span style={{ fontSize: '10px', fontWeight: 600, color: '#9498ac', textTransform: 'uppercase' }}>Show:</span>
                                            {FILTER_LABELS.map(f => (
                                                <label key={f.id} className="flex items-center gap-1 cursor-pointer" style={{ fontSize: '11px', color: '#565772' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={contentFilters[f.id] ?? false}
                                                        onChange={() => setContentFilters(prev => ({ ...prev, [f.id]: !prev[f.id] }))}
                                                        style={{ accentColor: '#4658ff' }}
                                                        className="w-3 h-3"
                                                    />
                                                    {f.label}
                                                </label>
                                            ))}
                                        </div>
                                        <div className="flex flex-1 overflow-hidden" style={{ gap: '0' }}>
                                            {showSourcePreview && activeSourceFile && (
                                                <div className="flex flex-col overflow-hidden" style={{ width: '50%', borderRight: '1px solid #e1e6f8' }}>
                                                    <div className="flex items-center justify-between flex-shrink-0" style={{ padding: '6px 12px', background: '#f7f9ff', borderBottom: '1px solid #e1e6f8' }}>
                                                        <span style={{ fontSize: '11px', fontWeight: 500, color: '#4658ff' }}>Source File</span>
                                                        <span style={{ fontSize: '10px', color: '#aeb4c9' }}>{activeSourceFile.name} &middot; {(activeSourceFile.size / 1024).toFixed(0)}KB</span>
                                                    </div>
                                                    <OcrSourcePreview
                                                        sourceFile={activeSourceFile}
                                                        pages={pages}
                                                        activePage={activePage}
                                                        hoveredBlockIdx={hoveredBlockIdx}
                                                        selectedBlockIdx={selectedBlockIdx}
                                                        onBlockHover={(pageIdx, idx) => { setActivePage(pageIdx); setHoveredBlockIdx(idx); }}
                                                        onBlockClick={(pageIdx, idx) => { setActivePage(pageIdx); setSelectedBlockIdx(idx === selectedBlockIdx ? null : idx); }}
                                                        scrollRef={leftScrollRef}
                                                        onScroll={() => {
                                                            if (scrollSyncRef.current) return;
                                                            scrollSyncRef.current = true;
                                                            const left = leftScrollRef.current;
                                                            const right = rightScrollRef.current;
                                                            if (left && right) {
                                                                const ratio = left.scrollTop / (left.scrollHeight - left.clientHeight || 1);
                                                                right.scrollTop = ratio * (right.scrollHeight - right.clientHeight || 1);
                                                            }
                                                            requestAnimationFrame(() => { scrollSyncRef.current = false; });
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            <div className="flex flex-col overflow-hidden" style={{ flex: 1 }}>
                                                {isProcessing && !hasResult ? (
                                                    <div className="flex-1 flex flex-col items-center justify-center" style={{ color: '#9498ac' }}>
                                                        <div className="relative mb-4">
                                                            <div style={{ width: 48, height: 48, border: '3px solid #e1e6f8', borderTop: '3px solid #4658ff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                                                        </div>
                                                        <p style={{ fontSize: '14px', color: '#565772', fontWeight: 500 }}>Parsing document...</p>
                                                        <p style={{ fontSize: '13px', color: '#4658ff', fontWeight: 600, marginTop: '8px' }}>
                                                            {activeTask.extractedPages || 0} / {activeTask.totalPages || '?'} pages
                                                        </p>
                                                        <p style={{ fontSize: '11px', color: '#aeb4c9', marginTop: '4px' }}>
                                                            PaddleOCR-VL-1.5 &middot; {STATUS_MAP[activeTask.taskStatus]}
                                                        </p>
                                                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                                    </div>
                                                ) : hasResult ? (
                                                    pages.some(p => p?.spottingRes) ? (
                                                        <div className="flex-1 overflow-y-auto" ref={rightScrollRef} onScroll={() => {
                                                            if (scrollSyncRef.current) return;
                                                            scrollSyncRef.current = true;
                                                            const left = leftScrollRef.current;
                                                            const right = rightScrollRef.current;
                                                            if (left && right) {
                                                                const ratio = right.scrollTop / (right.scrollHeight - right.clientHeight || 1);
                                                                left.scrollTop = ratio * (left.scrollHeight - left.clientHeight || 1);
                                                            }
                                                            requestAnimationFrame(() => { scrollSyncRef.current = false; });
                                                        }}>
                                                            {pages.map((p, i) => (
                                                                <OcrSpottingView
                                                                    key={i}
                                                                    pageData={p}
                                                                    pageIndex={i}
                                                                    hoveredIdx={activePage === i ? hoveredBlockIdx : null}
                                                                    onHover={(idx) => { setActivePage(i); setHoveredBlockIdx(idx); }}
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <OcrPageView
                                                            pages={pages}
                                                            filters={contentFilters}
                                                            activePage={activePage}
                                                            hoveredBlockIdx={hoveredBlockIdx}
                                                            selectedBlockIdx={selectedBlockIdx}
                                                            onBlockHover={setHoveredBlockIdx}
                                                            onBlockClick={(idx) => { setSelectedBlockIdx(idx === selectedBlockIdx ? null : idx); }}
                                                        />
                                                    )
                                                ) : (
                                                    <div className="flex-1 flex flex-col items-center justify-center" style={{ color: '#aeb4c9' }}>
                                                        {!activeFile ? (
                                                            <>
                                                                <div className="text-4xl mb-2">📋</div>
                                                                <p style={{ fontSize: '13px' }}>Select a file to view results</p>
                                                            </>
                                                        ) : (
                                                            <p style={{ fontSize: '13px' }}>Click "Start" to begin parsing</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                                {resultTab === 'json' && (
                                    <div className="flex-1 overflow-y-auto" style={{ padding: '20px' }}>
                                        <pre style={{ fontSize: '12px', color: '#565772', whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(currentPage, null, 2)}
                                        </pre>
                                    </div>
                                )}
                                {resultTab === 'info' && hasResult && (
                                    <div className="flex-1 overflow-y-auto" style={{ padding: '20px' }}>
                                        <div className="max-w-2xl space-y-4">
                                            <div className="grid grid-cols-2 gap-3">
                                                {[
                                                    { label: 'Task ID', value: activeResult.taskId },
                                                    { label: 'Batch ID', value: activeResult.batchId },
                                                    { label: 'File Name', value: activeResult.fileName },
                                                    { label: 'File Size', value: activeResult.fileSize ? `${(activeResult.fileSize / 1024 / 1024).toFixed(2)} MB` : '-' },
                                                    { label: 'Parse Model', value: activeResult.parseModel },
                                                    { label: 'Status', value: STATUS_MAP[activeResult.taskStatus] || activeResult.taskStatus },
                                                    { label: 'Total Pages', value: activeResult.totalPages },
                                                    { label: 'Extracted Pages', value: activeResult.extractedPages },
                                                    { label: 'Inference Time', value: activeResult.inferenceTime ? `${activeResult.inferenceTime}s` : '-' },
                                                    { label: 'Avg Time / Page', value: activeResult.inferenceTime && activeResult.totalPages ? `${(activeResult.inferenceTime / activeResult.totalPages).toFixed(2)}s` : '-' },
                                                    { label: 'Created', value: activeResult.createTime },
                                                    { label: 'Error', value: activeResult.error || 'None' },
                                                ].map(({ label, value }) => (
                                                    <div key={label} style={{ background: '#f7f9ff', borderRadius: '8px', padding: '10px 12px' }}>
                                                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#9498ac', textTransform: 'uppercase' }}>{label}</div>
                                                        <div style={{ fontSize: '13px', color: '#140e35', marginTop: '4px', wordBreak: 'break-all' }}>{value}</div>
                                                    </div>
                                                ))}
                                            </div>
                                            {activeResult.parseParams && (() => {
                                                try {
                                                    const params = JSON.parse(activeResult.parseParams);
                                                    return (
                                                        <div>
                                                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#9498ac', textTransform: 'uppercase', marginBottom: '8px' }}>Parse Parameters</div>
                                                            <div style={{ background: '#f7f9ff', borderRadius: '8px', padding: '12px' }}>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {Object.entries(params).map(([k, v]) => (
                                                                        <span key={k} style={{
                                                                            padding: '2px 8px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '10px',
                                                                            fontWeight: 500,
                                                                            background: v === true ? '#e6f7ed' : v === false ? '#f0f1f5' : '#e4ecff',
                                                                            color: v === true ? '#2FBD71' : v === false ? '#9498ac' : '#4658ff',
                                                                        }}>
                                                                            {k}: {String(v)}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                } catch { return null; }
                                            })()}
                                        </div>
                                    </div>
                                )}

                                {(pages.length > 1 || (activeTask?.totalPages > 1)) && (
                                    <div className="flex items-center gap-1 overflow-x-auto flex-shrink-0" style={{ padding: '4px 12px', borderTop: '1px solid #e1e6f8' }}>
                                        <span style={{ fontSize: '12px', color: '#9498ac', marginRight: '8px', whiteSpace: 'nowrap' }}>{activeFile}</span>
                                        {(pages.length > 0 ? pages : Array.from({ length: activeTask?.totalPages || 0 })).map((_, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setActivePage(i)}
                                                style={{
                                                    padding: '4px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    border: 'none',
                                                    background: activePage === i ? '#4658ff' : '#e5e9f3',
                                                    color: activePage === i ? '#fff' : '#565772',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </div>
            </div>

            {contextMenu && (
                <div
                    className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                >
                    <button
                        className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => runSingleFile(contextMenu.fileName, false)}
                    >
                        <span>▶</span> Run
                    </button>
                    <button
                        className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => runSingleFile(contextMenu.fileName, true)}
                    >
                        <span>🔄</span> Force Re-run
                    </button>
                    {contextMenu.status === 'completed' && (
                        <button
                            className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            onClick={() => { setActiveFile(contextMenu.fileName); setContextMenu(null); }}
                        >
                            <span>👁</span> View Result
                        </button>
                    )}
                    <div className="border-t border-gray-100 my-1" />
                    <button
                        className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                        onClick={() => { const idx = files.findIndex(f => f.name === contextMenu.fileName); if (idx >= 0) removeFile(idx); setContextMenu(null); }}
                    >
                        <span>✕</span> Remove
                    </button>
                </div>
            )}

            <Modal open={showMonitor} size="lg" onClose={() => setShowMonitor(false)} title="OCR Server Monitor">
                <div className="space-y-4">
                    <div className="flex justify-end">
                        <Button size="xs" variant="ghost" onClick={loadMonitor}>Refresh</Button>
                    </div>
                    {monitorData ? (
                        <>
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Service Health</h4>
                                <div className="grid grid-cols-2 gap-2">
                                    {monitorData.health && Object.entries(monitorData.health).map(([k, v]) => (
                                        <div key={k} className="bg-gray-50 rounded-lg px-3 py-2">
                                            <div className="text-[10px] text-gray-400 uppercase">{k}</div>
                                            <div className={`text-sm font-medium ${v === true || v === 'ok' ? 'text-green-600' : v === false ? 'text-red-600' : 'text-gray-900'}`}>
                                                {String(v)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Cache</h4>
                                <div className="grid grid-cols-4 gap-2">
                                    {monitorData.cache && Object.entries(monitorData.cache).map(([k, v]) => (
                                        <div key={k} className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                                            <div className="text-lg font-bold text-blue-600">{v}</div>
                                            <div className="text-[10px] text-gray-400">{k}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center text-gray-400 py-8">Loading...</div>
                    )}
                </div>
            </Modal>

            <Modal open={showApiDocs} size="xl" onClose={() => setShowApiDocs(false)} title="OCR API Documentation" fullHeight>
                <div className="px-2 py-4">
                    <ApiDocsContent />
                </div>
            </Modal>

            <Modal open={showGetCode} size="xl" onClose={() => setShowGetCode(false)} title="Get Code" fullHeight>
                <div className="px-2 py-4 h-full">
                    <OcrGetCode params={settingsToParams(settings)} onClose={() => setShowGetCode(false)} />
                </div>
            </Modal>
        </div>
    );
}
